import { Fragment, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ChevronDown, ChevronRight, Download, Search } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuthStore } from '../../store/authStore'
import type { PageMeta } from '../../lib/permissions/pageMeta'
import { PageHeader } from '../../components/ui/PageHeader'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { downloadBlob } from '../../lib/staffTemplate/generate'
import { displayDate, today } from '../../utils/dates'
import { cn } from '../../utils/cn'
import toast from 'react-hot-toast'

// Product Movement Report — a per-product "bank statement of quantity". Reads the
// additive, read-only RPC get_product_movement_report (mig 391): opening balance,
// per-type summary, chronological running-balance statement, and honest tie flags.
// Purpose: reconcile Palstro against the owner's old system from go-live, product
// by product.

export const pageMeta: PageMeta = {
  key: 'inventory.product_movement',
  label: 'Product Movement',
  module: 'Trading',
  section: 'Inventory',
  description: 'Per-product quantity statement — every movement, running balance, tie-out',
  defaultPermissions: {
    owner: 'view', admin: 'view', manager: 'view',
    accountant: 'none', cashier: 'none', viewer: 'none',
  },
}

type Movement = {
  date: string
  type: string
  reference_label: string | null
  reference_type: string | null
  reference_id: string | null
  route_id: string | null
  qty_in: number
  qty_out: number
  balance: number
  by: string | null
  note: string | null
}
type SummaryRow = { type: string; entries: number; qty_in: number; qty_out: number; net: number }
type Report = {
  opening: { qty: number; as_of: string }
  rows: Movement[]
  summary: SummaryRow[]
  closing_qty: number
  live_qty: number
  ties: boolean
  ties_live: boolean
  from: string
  to: string
}
type Product = { id: string; name: string; sku: string | null }

type Preset = 'month' | 'quarter' | 'year' | 'all' | 'custom'
const PRESETS: { value: Preset; label: string }[] = [
  { value: 'month', label: 'This month' },
  { value: 'quarter', label: 'This quarter' },
  { value: 'year', label: 'This year' },
  { value: 'all', label: 'All since go-live' },
  { value: 'custom', label: 'Custom' },
]

const MOVEMENT_LABELS: Record<string, string> = {
  opening: 'Opening balance', purchase: 'Purchase received', sale: 'Sale',
  cancel_restore: 'Sale cancelled (restock)', reopen_restore: 'Sale reopened (restock)',
  purchase_cancel: 'Purchase cancelled', adjustment: 'Stock adjustment',
  return_in: 'Customer return', purchase_return: 'Return to supplier',
  transfer_in: 'Transfer in', transfer_out: 'Transfer out', git_receipt: 'Goods received (GIT)',
}
function typeLabel(t: string): string {
  return MOVEMENT_LABELS[t] ?? t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// Fixed business order for the summary (screen + Excel), NOT by size:
// Opening → Purchases → Goods in transit → Sales → Adjustments → Stock counts →
// Returns → any other type (alpha). Related reversals sit with their family.
const SUMMARY_ORDER: Record<string, number> = {
  opening: 0,
  purchase: 10, purchase_cancel: 11,
  git_receipt: 20, goods_in_transit: 20, git_variance: 21,
  sale: 30, cancel_restore: 31, reopen_restore: 32,
  adjustment: 40,
  stock_count: 50, stock_take: 50,
  return_in: 60, sale_return: 60, purchase_return: 61,
}
function summaryRank(t: string): number { return SUMMARY_ORDER[t] ?? 900 }
function orderSummary(rows: SummaryRow[]): SummaryRow[] {
  return [...rows].sort((a, b) => summaryRank(a.type) - summaryRank(b.type) || typeLabel(a.type).localeCompare(typeLabel(b.type)))
}

// reference_type → route (page-side, using the ids the RPC returns; no client lookup).
function refRoute(reference_type: string | null, routeId: string | null): string | null {
  if (!routeId) {
    return reference_type === 'stock_adjustment' ? '/stock' : null
  }
  switch (reference_type) {
    case 'sales_order': case 'sale_cancel': case 'sale_reopen': return `/sales/${routeId}`
    case 'purchase_order': case 'purchase_order_cancel': return `/purchases/${routeId}`
    case 'sale_return': return `/sales/returns/${routeId}`
    case 'stock_adjustment': return '/stock'
    default: return null
  }
}

function pad(n: number): string { return String(n).padStart(2, '0') }
function resolveRange(p: Preset, customFrom: string, customTo: string): { from: string; to: string } {
  const now = new Date(); const y = now.getFullYear(); const mo = now.getMonth(); const t = today()
  switch (p) {
    case 'month': return { from: `${y}-${pad(mo + 1)}-01`, to: t }
    case 'quarter': { const qm = Math.floor(mo / 3) * 3; return { from: `${y}-${pad(qm + 1)}-01`, to: t } }
    case 'year': return { from: `${y}-01-01`, to: t }
    case 'all': return { from: '2000-01-01', to: t }
    case 'custom': return { from: customFrom || '2000-01-01', to: customTo || t }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rpc = (fn: string, args: Record<string, unknown>) => (supabase as any).rpc(fn, args)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

function fmtDateTime(iso: string): string {
  const [d, tm] = iso.split('T')
  return tm && tm.slice(0, 5) !== '00:00' ? `${d} ${tm.slice(0, 5)}` : d
}
const qfmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 })

export default function ProductMovementReportPage() {
  const { tenant } = useAuthStore()
  const tenantId = tenant?.id ?? ''

  const [productId, setProductId] = useState<string | null>(null)
  const [productSearch, setProductSearch] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [preset, setPreset] = useState<Preset>('all')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)
  const [physical, setPhysical] = useState('')
  const [downloading, setDownloading] = useState(false)

  const range = useMemo(() => resolveRange(preset, customFrom, customTo), [preset, customFrom, customTo])

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['pm-products', tenantId],
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await db('products').select('id, name, sku').eq('tenant_id', tenantId).order('name')
      if (error) throw error
      return (data ?? []) as Product[]
    },
  })
  const selectedProduct = products.find(p => p.id === productId) ?? null
  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase()
    const list = q ? products.filter(p => p.name.toLowerCase().includes(q) || (p.sku ?? '').toLowerCase().includes(q)) : products
    return list.slice(0, 50)
  }, [products, productSearch])

  const { data, isLoading, isError } = useQuery<Report>({
    queryKey: ['product-movement', tenantId, productId, range.from, range.to],
    enabled: !!tenantId && !!productId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await rpc('get_product_movement_report', {
        p_tenant_id: tenantId, p_product_id: productId, p_from: range.from, p_to: range.to,
      })
      if (error) throw error
      return data as Report
    },
  })

  const rowsByType = useMemo(() => {
    const m = new Map<string, Movement[]>()
    for (const r of data?.rows ?? []) {
      const arr = m.get(r.type); if (arr) arr.push(r); else m.set(r.type, [r])
    }
    return m
  }, [data])
  // Summary in fixed business order (screen + Excel). Statement stays chronological.
  const orderedSummary = useMemo(() => orderSummary(data?.summary ?? []), [data])

  const isThroughToday = range.to >= today()
  const banner: { tone: 'red' | 'amber'; text: string } | null = useMemo(() => {
    if (!data) return null
    if (!data.ties) {
      const gap = (data.opening.qty + data.rows.reduce((s, r) => s + r.qty_in - r.qty_out, 0)) - data.closing_qty
      return { tone: 'red', text: `This product's movements do not fully reconcile to its statement balance — gap of ${qfmt(gap)}.` }
    }
    if (isThroughToday && !data.ties_live) {
      return { tone: 'red', text: `Statement balance (${qfmt(data.closing_qty)}) does not match current stock (${qfmt(data.live_qty)}) — gap of ${qfmt(data.closing_qty - data.live_qty)}.` }
    }
    return null
  }, [data, isThroughToday])

  const physicalNum = physical.trim() === '' ? null : Number(physical)
  const reconcile = physicalNum === null || Number.isNaN(physicalNum) || !data
    ? null
    : { gap: physicalNum - data.closing_qty, matches: Math.abs(physicalNum - data.closing_qty) < 0.001 }

  const exportExcel = async () => {
    if (!data || !selectedProduct || downloading) return
    setDownloading(true)
    try {
      const ExcelJS = (await import('exceljs')).default
      const wb = new ExcelJS.Workbook()

      // Summary sheet (grouped movement types → entries)
      const sum = wb.addWorksheet('Summary', { views: [{ state: 'frozen', ySplit: 1 }] })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(sum.properties as any).outlineProperties = { summaryBelow: false, summaryRight: false }
      sum.addRow(['Movement type / Date', 'Reference / Entries', 'In', 'Out', 'Net / Balance']).font = { bold: true }
      sum.addRow([`Opening balance as at ${data.opening.as_of}`, '', '', '', data.opening.qty]).font = { italic: true }
      for (const s of orderSummary(data.summary)) {
        const g = sum.addRow([typeLabel(s.type), s.entries, s.qty_in, s.qty_out, s.net])
        g.font = { bold: true }
        for (const r of rowsByType.get(s.type) ?? []) {
          const dr = sum.addRow([fmtDateTime(r.date), r.reference_label ?? '', r.qty_in || '', r.qty_out || '', r.balance])
          dr.outlineLevel = 1; dr.hidden = true
        }
      }
      sum.addRow(['TOTAL (current quantity)', '', '', '', data.closing_qty]).font = { bold: true }
      ;[30, 22, 12, 12, 16].forEach((w, i) => { sum.getColumn(i + 1).width = w })

      // Statement sheet (chronological running balance)
      const st = wb.addWorksheet('Statement', { views: [{ state: 'frozen', ySplit: 1 }] })
      st.addRow(['Date', 'Type', 'Reference', 'By / Note', 'In', 'Out', 'Balance']).font = { bold: true }
      st.addRow([`Opening balance as at ${data.opening.as_of}`, '', '', '', '', '', data.opening.qty]).font = { italic: true }
      for (const r of data.rows) {
        st.addRow([fmtDateTime(r.date), typeLabel(r.type), r.reference_label ?? '', r.by ?? r.note ?? '', r.qty_in || '', r.qty_out || '', r.balance])
      }
      ;[18, 22, 18, 24, 12, 12, 14].forEach((w, i) => { st.getColumn(i + 1).width = w })

      const buf = await wb.xlsx.writeBuffer()
      const tag = (selectedProduct.sku || selectedProduct.name).replace(/[^a-z0-9]+/gi, '-').toLowerCase()
      downloadBlob(
        new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `product-movement-${tag}-${range.from}_to_${range.to}.xlsx`,
      )
    } catch (e) {
      toast.error('Could not build the file: ' + (e instanceof Error ? e.message : String(e)))
    } finally {
      setDownloading(false)
    }
  }

  const RefLink = ({ r }: { r: Movement }) => {
    const route = refRoute(r.reference_type, r.route_id)
    const label = r.reference_label || '—'
    return route
      ? <Link to={route} className="text-brand-600 hover:underline" onClick={e => e.stopPropagation()}>{label}</Link>
      : <span className="text-gray-600">{label}</span>
  }

  return (
    <div className="space-y-5">
      <PageHeader title="Product Movement" subtitle="A statement of every quantity movement for one product — reconcile against your old system." />

      {/* Controls */}
      <div className="card p-4 space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          {/* Product picker */}
          <div className="relative w-full lg:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              className="input pl-9 py-1.5"
              placeholder={selectedProduct ? `${selectedProduct.name}${selectedProduct.sku ? ` · ${selectedProduct.sku}` : ''}` : 'Search a product…'}
              value={productSearch}
              onChange={e => { setProductSearch(e.target.value); setPickerOpen(true) }}
              onFocus={() => setPickerOpen(true)}
              onBlur={() => setTimeout(() => setPickerOpen(false), 150)}
            />
            {pickerOpen && filteredProducts.length > 0 && (
              <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-card">
                {filteredProducts.map(p => (
                  <button
                    key={p.id} type="button"
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                    onMouseDown={() => { setProductId(p.id); setProductSearch(''); setPickerOpen(false); setExpanded(null) }}
                  >
                    <div className="font-medium text-gray-900 truncate">{p.name}</div>
                    {p.sku && <div className="text-xs text-gray-400">{p.sku}</div>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1">
              {PRESETS.map(p => (
                <button key={p.value} type="button" onClick={() => setPreset(p.value)}
                  className={cn('px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                    preset === p.value ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>{p.label}</button>
              ))}
            </div>
            <button type="button" onClick={exportExcel} disabled={!data || downloading}
              className="btn-secondary inline-flex items-center gap-2 disabled:opacity-50">
              <Download className="h-4 w-4" /> {downloading ? 'Preparing…' : 'Excel'}
            </button>
          </div>
        </div>
        {preset === 'custom' && (
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" className="input py-1.5 w-auto" aria-label="From date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} />
            <span className="text-gray-400 text-sm">to</span>
            <input type="date" className="input py-1.5 w-auto" aria-label="To date" value={customTo} onChange={e => setCustomTo(e.target.value)} />
          </div>
        )}
      </div>

      {!productId ? (
        <EmptyState title="Pick a product" description="Search and select a product to see its full movement statement." />
      ) : isLoading ? (
        <div className="flex justify-center py-16"><Spinner /></div>
      ) : isError ? (
        <EmptyState title="Couldn't load the report" description="Please try again in a moment." />
      ) : !data ? null : (
        <>
          {/* Honest tie banner */}
          {banner && (
            <div className={cn('flex items-start gap-2 rounded-xl border px-4 py-3 text-sm',
              banner.tone === 'red' ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800')}>
              <AlertTriangle className={cn('h-4 w-4 mt-0.5 shrink-0', banner.tone === 'red' ? 'text-red-500' : 'text-amber-500')} />
              <span>{banner.text}</span>
            </div>
          )}

          {/* Opening + reconcile strip */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="card p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Opening (as at {displayDate(data.opening.as_of)})</p>
              <p className="mt-1 text-xl font-semibold text-gray-900 tabular-nums">{qfmt(data.opening.qty)}</p>
            </div>
            <div className="card p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Current quantity</p>
              <p className="mt-1 text-xl font-semibold text-gray-900 tabular-nums">{qfmt(data.closing_qty)}</p>
              {!data.ties_live && isThroughToday && <p className="text-xs text-red-600 mt-0.5">live stock: {qfmt(data.live_qty)}</p>}
            </div>
            <div className="card p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Reconcile against physical / old system</p>
              <div className="mt-1 flex items-center gap-2">
                <input type="number" className="input py-1.5 w-32" placeholder="count…" value={physical} onChange={e => setPhysical(e.target.value)} />
                {reconcile && (reconcile.matches
                  ? <span className="badge badge-green">Matches</span>
                  : <span className="text-sm font-medium text-red-600 tabular-nums">Gap {reconcile.gap > 0 ? '+' : ''}{qfmt(reconcile.gap)}</span>)}
              </div>
            </div>
          </div>

          {/* Summary table (expandable) */}
          <div className="card p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100"><h2 className="section-title">Movements by type</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-gray-200">
                    <th className="px-3 py-2 text-left font-medium">Movement type</th>
                    <th className="px-3 py-2 text-right font-medium">Entries</th>
                    <th className="px-3 py-2 text-right font-medium">In</th>
                    <th className="px-3 py-2 text-right font-medium">Out</th>
                    <th className="px-3 py-2 text-right font-medium">Net</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr className="text-gray-500 italic">
                    <td className="px-3 py-2">Opening balance (as at {displayDate(data.opening.as_of)})</td>
                    <td className="px-3 py-2 text-right">—</td>
                    <td className="px-3 py-2 text-right tabular-nums">{data.opening.qty > 0 ? qfmt(data.opening.qty) : ''}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{data.opening.qty < 0 ? qfmt(-data.opening.qty) : ''}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{qfmt(data.opening.qty)}</td>
                  </tr>
                  {orderedSummary.map(s => {
                    const open = expanded === s.type
                    return (
                      <Fragment key={s.type}>
                        <tr onClick={() => setExpanded(open ? null : s.type)} className="cursor-pointer hover:bg-gray-50">
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              {open ? <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />}
                              <span className="font-medium text-gray-900">{typeLabel(s.type)}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums">{s.entries.toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-sage-700">{s.qty_in ? qfmt(s.qty_in) : ''}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-red-600">{s.qty_out ? qfmt(s.qty_out) : ''}</td>
                          <td className="px-3 py-2.5 text-right tabular-nums font-medium">{qfmt(s.net)}</td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={5} className="bg-gray-50 px-3 py-2">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-gray-400 border-b border-gray-200">
                                    <th className="px-2 py-1.5 text-left font-medium">Date</th>
                                    <th className="px-2 py-1.5 text-left font-medium">Reference</th>
                                    <th className="px-2 py-1.5 text-left font-medium">By</th>
                                    <th className="px-2 py-1.5 text-right font-medium">In</th>
                                    <th className="px-2 py-1.5 text-right font-medium">Out</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {(rowsByType.get(s.type) ?? []).map((r, i) => (
                                    <tr key={i}>
                                      <td className="px-2 py-1.5 whitespace-nowrap">{fmtDateTime(r.date)}</td>
                                      <td className="px-2 py-1.5"><RefLink r={r} /></td>
                                      <td className="px-2 py-1.5 text-gray-500">{r.by ?? '—'}</td>
                                      <td className="px-2 py-1.5 text-right tabular-nums text-sage-700">{r.qty_in ? qfmt(r.qty_in) : ''}</td>
                                      <td className="px-2 py-1.5 text-right tabular-nums text-red-600">{r.qty_out ? qfmt(r.qty_out) : ''}</td>
                                    </tr>
                                  ))}
                                  <tr className="font-medium bg-gray-100/60">
                                    <td className="px-2 py-1.5" colSpan={3}>Subtotal — {typeLabel(s.type)}</td>
                                    <td className="px-2 py-1.5 text-right tabular-nums">{s.qty_in ? qfmt(s.qty_in) : ''}</td>
                                    <td className="px-2 py-1.5 text-right tabular-nums">{s.qty_out ? qfmt(s.qty_out) : ''}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 font-semibold text-gray-900 bg-gray-50/60">
                    <td className="px-3 py-3" colSpan={4}>Total (current quantity)</td>
                    <td className="px-3 py-3 text-right tabular-nums">{qfmt(data.closing_qty)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Chronological statement */}
          <div className="card p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100"><h2 className="section-title">Statement</h2></div>
            {!data.rows.length ? (
              <EmptyState title="No movements in this period" description="Try a wider date range." className="py-8" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm report-grid">
                  <thead>
                    <tr className="text-xs text-gray-500 border-b border-gray-200">
                      <th className="px-3 py-2 text-left font-medium">Date</th>
                      <th className="px-3 py-2 text-left font-medium">Type</th>
                      <th className="px-3 py-2 text-left font-medium">Reference</th>
                      <th className="px-3 py-2 text-left font-medium">By / note</th>
                      <th className="px-3 py-2 text-right font-medium">In</th>
                      <th className="px-3 py-2 text-right font-medium">Out</th>
                      <th className="px-3 py-2 text-right font-medium">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr className="text-gray-500 italic bg-gray-50/40">
                      <td className="px-3 py-2" colSpan={6}>Opening balance (as at {displayDate(data.opening.as_of)})</td>
                      <td className="px-3 py-2 text-right tabular-nums">{qfmt(data.opening.qty)}</td>
                    </tr>
                    {data.rows.map((r, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5 whitespace-nowrap">{fmtDateTime(r.date)}</td>
                        <td className="px-3 py-2.5">{typeLabel(r.type)}</td>
                        <td className="px-3 py-2.5"><RefLink r={r} /></td>
                        <td className="px-3 py-2.5 text-gray-500 truncate max-w-[16rem]">{r.by ?? r.note ?? '—'}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-sage-700">{r.qty_in ? qfmt(r.qty_in) : ''}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-red-600">{r.qty_out ? qfmt(r.qty_out) : ''}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-medium">{qfmt(r.balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 font-semibold text-gray-900 bg-gray-50/60">
                      <td className="px-3 py-3" colSpan={6}>Closing balance</td>
                      <td className="px-3 py-3 text-right tabular-nums">{qfmt(data.closing_qty)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
